import React, { useCallback, useState } from "react";

import { InputNumber } from "primereact/inputnumber";
import ExpandableDataTable from "../../tables/ExpandableDataTable";
import OperationalDataTableSkeleton from "../../operational/OperationalDataTableSkeleton";
import OrderItemPriceSplitsTable from "../orders/OrderItemPriceSplitsTable";
import {
  classNames,
  formatCurrency,
  formatDateWithTime,
} from "../../../shared/utility";

/**
 * PrimeReact DataTable keeps expanded rows in an internal ref that syncs from
 * props in useEffect (after paint). A second row can be expanded before that ref
 * catches up, so onRowToggle may only contain the latest row. Merge with React
 * state for new expansions; when the incoming map is a strict subset of keys
 * (fewer rows expanded), treat it as a collapse and use it as-is.
 */
function mergeExpandedRowState(prev, next) {
  const prevKeys = Object.keys(prev);
  const nextKeys = Object.keys(next);
  const nextIsStrictCollapse =
    nextKeys.length < prevKeys.length &&
    nextKeys.every((k) => Object.prototype.hasOwnProperty.call(prev, k));
  if (nextIsStrictCollapse) {
    return next;
  }
  return { ...prev, ...next };
}

export default function OrderItemsForShipping(props) {
  const {
    askDeleteShippingItem,
    orderItems,
    order_id,
    onExpandRow,
    shippingItemsLoading = {},
    setProductTemperature,
    onProductTemperatureUpdated,
    user_type,
    onPrintShippingLabel,
    setEmptyModelOpen,
    onCreatePriceSplit,
    onUpdatePriceSplit,
    onDeletePriceSplit,
    canEditPrices = false,
  } = props;

  const [expandedRows, setExpandedRows] = useState({});
  const [printingRowId, setPrintingRowId] = useState(null);
  const [temperatureDrafts, setTemperatureDrafts] = useState({});
  const [savingTemperatureId, setSavingTemperatureId] = useState(null);

  const showShippingLabelButton =
    parseInt(String(user_type ?? 0), 10) !== 0 && onPrintShippingLabel;
  const showPrices = parseInt(String(user_type ?? 0), 10) !== 0;

  const handleExpandedRowsChange = useCallback((nextExpanded) => {
    setExpandedRows((prev) => mergeExpandedRowState(prev, nextExpanded ?? {}));
  }, []);

  const allowExpansion = () => true;

  const shippingItemActionTemplate = (rowData) => {
    return (
      <div className="ml-10">
        <button
          onClick={() => askDeleteShippingItem(rowData)}
          className="ml-2 rounded-md bg-red-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-[#FEF2F2]0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600"
        >
          Delete
        </button>
      </div>
    );
  };

  const lotNumberTemplate = (rowData) => {
    return <p>{rowData.lot_number}</p>;
  };

  const packagingDateTemplate = (rowData) => {
    return <p>{formatDateWithTime(rowData.packaging_date)}</p>;
  };

  const packagingSizeTemplate = (rowData) => {
    return <p>{Number(rowData.packing_size).toFixed(2)} kg</p>;
  };

  const barcodeBody = (rowData) => {
    let fontColor = null;
    return (
      <p className={classNames(fontColor, "font-medium")}>{rowData.barcode}</p>
    );
  };

  const productDetailsBody = (rowData) => {
    return (
      <p>
        <span className="text-gray-900 font-semibold">
          {rowData.product_name}
        </span>
        <br />
        <span className="text-gray-700">PLU:{rowData.plu}</span>
      </p>
    );
  };

  const saveProductTemperature = async (rowData) => {
    const temperature = Object.prototype.hasOwnProperty.call(
      temperatureDrafts,
      rowData.id,
    )
      ? temperatureDrafts[rowData.id]
      : rowData.product_temperature;

    if (
      temperature == null ||
      Number(temperature) === Number(rowData.product_temperature)
    ) {
      return;
    }

    setSavingTemperatureId(rowData.id);
    try {
      const result = await setProductTemperature(
        rowData.product_id,
        temperature,
        order_id,
      );

      if (result?.success) {
        const index = orderItems.findIndex((item) => item.id === rowData.id);
        if (index >= 0) onProductTemperatureUpdated(index, temperature);
        setTemperatureDrafts((prev) => {
          const next = { ...prev };
          delete next[rowData.id];
          return next;
        });
      }
    } finally {
      setSavingTemperatureId(null);
    }
  };

  const productTemperatureBody = (rowData) => (
    <div className="flex min-w-[9rem] items-center gap-2">
      <InputNumber
        value={
          Object.prototype.hasOwnProperty.call(temperatureDrafts, rowData.id)
            ? temperatureDrafts[rowData.id]
            : (rowData.product_temperature ?? null)
        }
        onValueChange={(e) =>
          setTemperatureDrafts((prev) => ({
            ...prev,
            [rowData.id]: e.value,
          }))
        }
        onBlur={() => saveProductTemperature(rowData)}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === "Enter") {
            e.preventDefault();
            e.currentTarget.blur();
          }
        }}
        minFractionDigits={0}
        maxFractionDigits={1}
        suffix=" °C"
        placeholder="Temperature"
        disabled={savingTemperatureId === rowData.id}
        inputClassName="w-28 rounded-lg border-slate-300 text-sm"
      />
      {savingTemperatureId === rowData.id ? (
        <span className="text-xs font-medium text-slate-500">Saving…</span>
      ) : null}
    </div>
  );

  const handleShippingLabelClick = async (rowData) => {
    if (!onPrintShippingLabel) return;
    setPrintingRowId(rowData.id);
    try {
      await onPrintShippingLabel(rowData);
    } finally {
      setPrintingRowId(null);
    }
  };

  const shippingLabelBody = (rowData) => (
    <button
      type="button"
      disabled={printingRowId === rowData.id}
      onClick={() => handleShippingLabelClick(rowData)}
      className="rounded-md bg-main-purple px-2 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50"
    >
      Shipping Label
    </button>
  );

  const orderQuantityBody = (rowData) => {
    return (
      <p>
        {rowData.quantity} {rowData.selling_unit_of_measure}
      </p>
    );
  };

  const shippedQuantity = (rowData) => {
    return (
      <p>
        {rowData.shipped_amount} {rowData.selling_unit_of_measure}
      </p>
    );
  };

  const unitPriceBody = (rowData) => (
    <p className="whitespace-nowrap font-medium text-slate-700">
      {rowData.allocated_quantity > 0
        ? formatCurrency(
            Number(rowData.total_order_shipped_amount ?? 0) /
              Number(rowData.allocated_quantity),
          )
        : "—"}
    </p>
  );

  const totalPriceBody = (rowData) => (
    <p className="whitespace-nowrap font-semibold text-slate-900">
      {formatCurrency(rowData.total_order_shipped_amount ?? 0)}
    </p>
  );

  const shippingItemQuantityBody = (rowData) => {
    return <p>{Number(rowData.case_quantity ?? 0).toFixed(2)}</p>;
  };

  const statusTextBody = (field) => (rowData) => {
    const value = rowData[field];
    if (!value) return <p>-</p>;
    return <p className="capitalize">{String(value).replaceAll("_", " ")}</p>;
  };

  const shippingItemColumns = [
    { header: "Barcode", body: barcodeBody },
    { header: "Carton Count", body: shippingItemQuantityBody },
    { header: "Packing Size", body: packagingSizeTemplate },
    { header: "Lot Number", body: lotNumberTemplate },
    { header: "Packaged date", body: packagingDateTemplate },
    { header: "Status", body: statusTextBody("status") },
    { header: "Return", body: statusTextBody("return_status") },
    { header: "Credit", body: statusTextBody("credit_status") },
    { header: "", body: shippingItemActionTemplate },
  ];

  const renderExpandedOrderItemRow = (orderItem) => {
    const shippingItems = orderItem?.shippingItems ?? [];
    const isLoading = Boolean(shippingItemsLoading[orderItem?.id]);

    if (isLoading) {
      return (
        <div className="p-3">
          <OperationalDataTableSkeleton
            columns={[
              "Barcode",
              "Carton Count",
              "Packing Size",
              "Lot Number",
              "Packaged date",
              "Status",
              "Return",
              "Credit",
            ]}
            rows={4}
          />
        </div>
      );
    }

    return (
      <div className="space-y-3 p-3">
        {showPrices ? (
          <OrderItemPriceSplitsTable
            orderItem={orderItem}
            canEdit={canEditPrices}
            setEmptyModelOpen={setEmptyModelOpen}
            onCreate={onCreatePriceSplit}
            onUpdate={onUpdatePriceSplit}
            onDelete={onDeletePriceSplit}
          />
        ) : null}
        <ExpandableDataTable
          data={shippingItems}
          dataKey="id"
          columns={shippingItemColumns}
          tableClassName="min-w-full bg-white divide-y divide-gray-300"
          cellClassName="py-2 bg-gray-100 border-1-bottom-gray px-2 text-sm"
        />
      </div>
    );
  };

  const productColWidth = showShippingLabelButton ? "30%" : "35%";
  const standardColWidth = showShippingLabelButton ? "18%" : "20%";

  const orderItemColumns = [
    { header: "Product", body: productDetailsBody, width: productColWidth },
    {
      header: "Order quantity/Cs",
      body: orderQuantityBody,
      width: standardColWidth,
    },
    {
      header: "Shipped quantity",
      body: shippedQuantity,
      width: standardColWidth,
    },
    ...(showPrices
      ? [
          { header: "Unit price", body: unitPriceBody, width: "13%" },
          { header: "Total price", body: totalPriceBody, width: "13%" },
        ]
      : []),
    {
      field: "product_temperature",
      header: "Product Temperature",
      body: productTemperatureBody,
      width: standardColWidth,
    },
    ...(showShippingLabelButton
      ? [
          {
            header: "Shipping Label",
            body: shippingLabelBody,
            width: "16%",
          },
        ]
      : []),
  ];

  return (
    <div className="mt-5 flow-root">
      <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-4 sm:-mx-6 lg:-mx-8">
        <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
          <div className="overflow-hidden">
            <ExpandableDataTable
              data={orderItems}
              dataKey="id"
              columns={orderItemColumns}
              expandable
              allowExpansion={allowExpansion}
              renderExpandedRow={renderExpandedOrderItemRow}
              expandedRows={expandedRows}
              onExpandedRowsChange={handleExpandedRowsChange}
              onRowExpand={onExpandRow}
              tableClassName="min-w-full bg-white divide-y divide-gray-300 text-sm"
              cellClassName="p-2 bg-white border-1-bottom-gray text-sm"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
